/** Balance transfer dialog for moving funds between reseller accounts. */
import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Typography,
  Alert,
  CircularProgress,
  Divider,
  Chip,
} from "@mui/material";
import {
  AccountBalanceWallet,
  ArrowForward,
  CheckCircle,
  Error as ErrorIcon,
} from "@mui/icons-material";
import { resellersApi } from "../../api/client";

/** Reseller interface matching backend schema. */
export interface Reseller {
  id: string;
  tenant: string;
  account: string;
  account_type: string;
  balance: number;
  credit_limit: number | null;
  disabled: boolean;
  parent_id: string | null;
  level: number | null;
  margin_percent: number | null;
  margin_floor: number | null;
  max_depth: number | null;
  full_account_path: string;
  created_at: string;
  updated_at: string;
}

/** Transaction interface for history display. */
export interface Transaction {
  id: string;
  source_tenant: string;
  source_account: string;
  dest_tenant: string;
  dest_account: string;
  amount: number;
  status: string;
  cgrates_transaction_id: string | null;
  initiated_by: string | null;
  created_at: string;
  completed_at: string | null;
  error_message: string | null;
}

interface BalanceTransferDialogProps {
  open: boolean;
  onClose: () => void;
  reseller: Reseller;
  downstreamAccounts: Reseller[];
  onSuccess?: () => void;
}

/** Status chip for transaction history. */
function TransactionStatusChip({ status }: { status: string }) {
  const getStatusColor = (s: string) => {
    switch (s) {
      case "completed":
        return "success";
      case "pending":
        return "warning";
      case "failed":
      case "rolled_back":
        return "error";
      default:
        return "default";
    }
  };

  return (
    <Chip
      label={status.toUpperCase()}
      color={getStatusColor(status) as any}
      size="small"
    />
  );
}

/** Balance transfer dialog component. */
export default function BalanceTransferDialog({
  open,
  onClose,
  reseller,
  downstreamAccounts,
  onSuccess,
}: BalanceTransferDialogProps) {
  const [destinationId, setDestinationId] = useState<string>("");
  const [amount, setAmount] = useState<string>("");
  const [step, setStep] = useState<"input" | "confirm">("input");
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>("");
  const [success, setSuccess] = useState<boolean>(false);
  const [recentTransfers, setRecentTransfers] = useState<Transaction[]>([]);

  // Validation states
  const amountNum = parseFloat(amount) || 0;
  const isValidAmount = amountNum > 0 && amountNum <= reseller.balance;
  const isValidDestination = destinationId !== "";
  const canSubmit = isValidAmount && isValidDestination && !loading;

  // Fetch recent transfers on mount
  useEffect(() => {
    if (open) {
      fetchRecentTransfers();
    }
  }, [open, reseller.id]);

  const fetchRecentTransfers = async () => {
    try {
      const response = await resellersApi.getTransfers(reseller.id, 5);
      setRecentTransfers(response.data.transfers || []);
    } catch (err) {
      console.error("Failed to fetch transfer history:", err);
    }
  };

  const handleTransfer = async () => {
    setLoading(true);
    setError("");
    setSuccess(false);

    try {
      await resellersApi.transferBalance(reseller.id, {
        destination_account_id: destinationId,
        amount: amountNum,
      });

      setSuccess(true);
      if (onSuccess) {
        onSuccess();
      }

      // Refresh transfer history
      await fetchRecentTransfers();

      // Reset form after 2 seconds
      setTimeout(() => {
        handleClose();
      }, 2000);
    } catch (err: any) {
      setError(err.response?.data?.detail || "Transfer failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setDestinationId("");
    setAmount("");
    setStep("input");
    setError("");
    setSuccess(false);
    onClose();
  };

  const destinationAccount = downstreamAccounts.find((acc) => acc.id === destinationId);

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <AccountBalanceWallet />
          Transfer Balance
        </Box>
      </DialogTitle>

      <DialogContent>
        {/* Source Account Info */}
        <Box mb={2}>
          <Typography variant="body2" color="text.secondary">
            From: {reseller.full_account_path}
          </Typography>
          <Typography variant="h6">
            Available: ${reseller.balance.toFixed(2)}
          </Typography>
        </Box>

        {step === "input" && (
          <>
            {/* Destination Account Selection */}
            <FormControl fullWidth margin="normal" disabled={loading}>
              <InputLabel>Destination Account</InputLabel>
              <Select
                value={destinationId}
                label="Destination Account"
                onChange={(e) => setDestinationId(e.target.value)}
              >
                {downstreamAccounts.length === 0 ? (
                  <MenuItem disabled>No downstream accounts available</MenuItem>
                ) : (
                  downstreamAccounts.map((account) => (
                    <MenuItem key={account.id} value={account.id}>
                      {account.full_account_path} (Balance: ${account.balance.toFixed(2)})
                    </MenuItem>
                  ))
                )}
              </Select>
            </FormControl>

            {/* Amount Input */}
            <TextField
              fullWidth
              margin="normal"
              label="Amount to Transfer"
              type="number"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              disabled={loading}
              inputProps={{ min: 0.01, max: reseller.balance, step: 0.01 }}
              helperText={
                amountNum > 0
                  ? `Minimum: $0.01 | Maximum: $${reseller.balance.toFixed(2)}`
                  : "Enter amount to transfer"
              }
              error={amount !== "" && amountNum <= 0}
            />

            {/* Validation Messages */}
            {amount !== "" && amountNum > reseller.balance && (
              <Alert severity="error" sx={{ mt: 1 }}>
                Amount exceeds available balance
              </Alert>
            )}

            {/* Recent Transfers */}
            {recentTransfers.length > 0 && (
              <Box mt={3}>
                <Divider sx={{ mb: 2 }} />
                <Typography variant="subtitle2" gutterBottom>
                  Recent Transfers
                </Typography>
                {recentTransfers.map((tx) => (
                  <Box
                    key={tx.id}
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      py: 1,
                      borderBottom: "1px solid #eee",
                    }}
                  >
                    <Box>
                      <Typography variant="body2">
                        {tx.dest_account === reseller.id ? "From" : "To"}:{" "}
                        {tx.dest_account === reseller.id ? tx.source_account : tx.dest_account}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {new Date(tx.created_at).toLocaleString()}
                      </Typography>
                    </Box>
                    <Box display="flex" alignItems="center" gap={1}>
                      <Typography variant="body2" fontWeight="bold">
                        ${tx.amount.toFixed(2)}
                      </Typography>
                      <TransactionStatusChip status={tx.status} />
                    </Box>
                  </Box>
                ))}
              </Box>
            )}
          </>
        )}

        {step === "confirm" && destinationAccount && (
          <>
            <Alert severity="info" sx={{ mb: 2 }}>
              Please review the transfer details before confirming
            </Alert>

            <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
              <Box>
                <Typography variant="body2" color="text.secondary">
                  From
                </Typography>
                <Typography variant="body1">
                  {reseller.full_account_path}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Balance: ${reseller.balance.toFixed(2)}
                </Typography>
              </Box>

              <ArrowForward color="action" />

              <Box textAlign="right">
                <Typography variant="body2" color="text.secondary">
                  To
                </Typography>
                <Typography variant="body1">
                  {destinationAccount.full_account_path}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Current Balance: ${destinationAccount.balance.toFixed(2)}
                </Typography>
              </Box>
            </Box>

            <Divider sx={{ my: 2 }} />

            <Typography variant="h6" align="center" color="primary">
              ${amountNum.toFixed(2)}
            </Typography>

            <Typography variant="body2" align="center" color="text.secondary">
              After transfer: From ${ (reseller.balance - amountNum).toFixed(2) } → To $
              { (destinationAccount.balance + amountNum).toFixed(2) }
            </Typography>
          </>
        )}

        {/* Error Message */}
        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}

        {/* Success Message */}
        {success && (
          <Alert
            icon={<CheckCircle />}
            severity="success"
            sx={{ mt: 2 }}
          >
            Transfer completed successfully!
          </Alert>
        )}

        {/* Loading Indicator */}
        {loading && (
          <Box display="flex" justifyContent="center" mt={2}>
            <CircularProgress size={24} />
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        {step === "input" ? (
          <>
            <Button onClick={handleClose} disabled={loading}>
              Cancel
            </Button>
            <Button
              onClick={() => setStep("confirm")}
              variant="contained"
              disabled={!canSubmit}
            >
              Review Transfer
            </Button>
          </>
        ) : (
          <>
            <Button onClick={() => setStep("input")} disabled={loading}>
              Back
            </Button>
            <Button
              onClick={handleTransfer}
              variant="contained"
              color="primary"
              disabled={loading || success}
              startIcon={loading ? <CircularProgress size={16} /> : <CheckCircle />}
            >
              {loading ? "Processing..." : "Confirm Transfer"}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
}
